import { useQueryClient } from "@tanstack/react-query";

import { useCloseDealOpportunities } from "@vega-api/generated/coinvest-deal-opportunity-actions/coinvest-deal-opportunity-actions";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type CloseDealModalProps = {
  isOpen: boolean;
  onClose: () => void;
  dealOpportunityId: string;
  dealId: string;
};

export const CloseDealModal = ({
  isOpen,
  onClose,
  dealOpportunityId,
  dealId,
}: CloseDealModalProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const { mutate: closeDeal, isPending: isLoading } =
    useCloseDealOpportunities();

  const handleConfirm = () => {
    closeDeal(
      {
        data: {
          dealOpportunityIds: [dealOpportunityId],
        },
      },
      {
        onSuccess: () => {
          onClose();
          showToast({
            variant: "success",
            title: m.coInvestments_tracking_dealClosedSuccessfully(),
          });
          queryClient.invalidateQueries({
            queryKey: getGetDealTrackingForGpQueryKey(dealId),
          });
        },
        onError: (error) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_tracking_closeDeal_errorMessage(),
          });
        },
      },
    );
  };

  return (
    <ActionaryModal
      dataTestId="close-deal-modal"
      description={
        <span className="body-base-regular text-text/secondary">
          {m.coInvestments_tracking_closeDeal_modalDescription()}
        </span>
      }
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_tracking_closeDeal_modalTitle()}
    >
      <div className="flex justify-end gap-3 pt-4">
        <ThemedButton
          dataTestId="cancel-close-deal-button"
          disabled={isLoading}
          onClick={onClose}
          size="default"
          type="button"
          variant="secondary"
        >
          {m.btn_cancel()}
        </ThemedButton>
        <ThemedButton
          dataTestId="confirm-close-deal-button"
          isLoading={isLoading}
          onClick={handleConfirm}
          size="default"
          type="button"
          variant="primary"
        >
          {m.btn_confirm()}
        </ThemedButton>
      </div>
    </ActionaryModal>
  );
};
